// `/api/v1/changes`: the approval card's read, and its two answers (#261, C4).
//
// Read off `control_plane/routes/changes.py`. `GET` answers the changes waiting
// on this session's side; `POST …/{id}/approve` and `…/decline` take the answer.
//
// **The answer names nothing but its proof.** The route forbids every key but
// the step-up grant and a decline's reason, and this module sends nothing else:
// the authority, the grant and the approver are the server's to read and record
// (#17 round 1 Q4, round 2 Q1).
//
// **Every answer is two calls**, in C3's shape (`api/policy.ts`): `POST
// /auth/step-up` for this change's own purpose mints the grant, and the grant
// rides the answer's body. The purpose names the change, so a proof for one
// change opens no other (#261 Q32).
//
// **A row is written to the projection** (`phase-2-cut.md` § C4): `request` is
// the `request_action` row's payload whole, the run page's `requestedAction`,
// and `phases` are the platform's `action_*` rows for it, the reducer's own
// `ActionState`s. The card reads those and nothing it had to be told twice.

import type { ActionState } from "../features/ops/investigationProjection";
import type { Api } from "./http";
import { STEP_UP_PATH, type StepUp } from "./policy";
import { ticketsFrom, type RunTicket } from "./runRecord";

export const CHANGES_PATH = "/api/v1/changes";

/** The two answers, as the path names them. */
export type ChangeAnswer = "approve" | "decline";

export const changeAnswerPath = (changeId: string, answer: ChangeAnswer): string =>
  `${CHANGES_PATH}/${encodeURIComponent(changeId)}/${answer}`;

/** `routes/changes.py::step_up_purpose`: the proof opens this change and no other. */
export const changeStepUpPurpose = (changeId: string): string => `change_approval:${changeId}`;

export interface ChangeHolder {
  kind: "workspace" | "org";
  id: string;
  name: string;
}

/** Who answers the change, how the answer is recorded, and whether this session may press. */
export interface ChangeApproval {
  /** `workspace`, or `grant` when an org holds `approve_writes` on the workspace. */
  authority: "workspace" | "grant";
  grant_id: string | null;
  /** The side whose members answer: the workspace, or the org the grant names. */
  holder: ChangeHolder;
  workspace: { id: string; name: string };
  /** The permission a member's role must carry to answer. */
  permission: string;
  step_up_purpose: string;
  may_answer: boolean;
}

export interface ChangeRow {
  change_id: string;
  run_id: string;
  /** The run's incident tickets, where each phase of the change is commented (#818). */
  tickets?: RunTicket[];
  status: string;
  requested_at: string;
  /** Unanswered by then, the run resumes with no action taken (#261 Q29). */
  expires_at: string;
  request: Record<string, unknown>;
  phases: ActionState[];
  approval: ChangeApproval;
  /**
   * Whether the platform applies the change once approved (`store/changes.py::PLATFORM_APPLIES`,
   * #910); otherwise a person applies it by hand, and the card says so before Approve.
   */
  platform_applies: boolean;
  /**
   * Whether the change's AWS account is connected (`store/changes.py::WaitingChange`, #1123).
   * While it is not, an approval is refused and the card offers Decline alone.
   */
  account_connected?: boolean;
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");
const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

function asPhase(value: unknown): ActionState | null {
  const row = record(value);
  if (typeof row.change_id !== "string" || typeof row.phase !== "string") return null;
  return {
    change_id: row.change_id,
    phase: row.phase,
    seq: typeof row.seq === "number" ? row.seq : 0,
    payload: record(row.payload),
  };
}

function asChange(value: unknown): ChangeRow | null {
  const row = record(value);
  if (typeof row.change_id !== "string") return null;
  const approval = record(row.approval);
  const holder = record(approval.holder);
  const workspace = record(approval.workspace);
  return {
    change_id: row.change_id,
    run_id: text(row.run_id),
    tickets: ticketsFrom(row.tickets),
    status: text(row.status),
    requested_at: text(row.requested_at),
    expires_at: text(row.expires_at),
    request: record(row.request),
    phases: Array.isArray(row.phases)
      ? row.phases.map(asPhase).filter((p): p is ActionState => p !== null)
      : [],
    // Absent or anything but `true` is "no": the card never promises an apply it was not told of.
    platform_applies: row.platform_applies === true,
    // Only an explicit `false` withholds Approve: a read that does not say offers it, and
    // the server refuses the approval in words (#1123).
    account_connected: row.account_connected !== false,
    approval: {
      authority: approval.authority === "grant" ? "grant" : "workspace",
      grant_id: typeof approval.grant_id === "string" ? approval.grant_id : null,
      holder: {
        kind: holder.kind === "org" ? "org" : "workspace",
        id: text(holder.id),
        name: text(holder.name),
      },
      workspace: { id: text(workspace.id), name: text(workspace.name) },
      permission: text(approval.permission),
      step_up_purpose: text(approval.step_up_purpose),
      // Absent or anything but `true` is "no": a button is never on by default.
      may_answer: approval.may_answer === true,
    },
  };
}

export function changesFrom(body: unknown): ChangeRow[] {
  return Array.isArray(body) ? body.map(asChange).filter((c): c is ChangeRow => c !== null) : [];
}

export const readChanges = async (api: Api): Promise<ChangeRow[]> =>
  changesFrom(await api.get<unknown>(CHANGES_PATH));

/**
 * Answer one change: step up for this change's purpose, then send the grant —
 * and a decline's reason — to the answer's path. Rejects before any call when
 * there is no credential to prove presence with.
 */
export async function answerChange(
  api: Api,
  changeId: string,
  answer: ChangeAnswer,
  stepUp: StepUp | null,
  reason: string,
): Promise<{ outcome: string }> {
  if (stepUp === null || stepUp.credential === "") {
    throw new Error("Answering a change needs your password, to prove it is you.");
  }
  const granted = await api.post<{ grant?: unknown }>(STEP_UP_PATH, {
    method: stepUp.method,
    credential: stepUp.credential,
    purpose: changeStepUpPurpose(changeId),
  });
  const body: { step_up_grant: string; reason?: string } = { step_up_grant: text(granted?.grant) };
  if (answer === "decline") body.reason = reason;
  const answered = await api.post<{ outcome?: unknown }>(changeAnswerPath(changeId, answer), body);
  return { outcome: text(answered?.outcome) };
}
